import React from 'react';

import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { TextComponent } from 'design/text/textComponents';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { MarginComponent } from 'design/margin';
import {
  SmallTableRowComponent,
  SmallTableWrapperComponent,
} from 'design/smallTable';
import { useGiroPaymentManager } from 'hooks/useGiroPaymentManager';
import { useGiroPaymentManagerDataProcess } from 'hooks/useGiroPaymentManagerDataProcess';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { normalizeProfileLike } from 'utils/profileLikeUtils';
import { useServices } from 'hooks/useServices';
import { ThemeProviderComponent } from 'design/themeProvider';
import { GiroPaymentManagerBillRow } from 'containers/GiroPaymentManager/GiroPaymentManagerBillRow';
import { RESIDENT_COUNTRY_CODE } from 'utils/constants';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';

type Props = {
  giroPaymentId: number;
  onSearchClient(): void;
  onEditRate(): void;
  onPaymentConfirmed(): void;
  onPaymentCancelled(): void;
  onViewClientDetails(clientId: number): void;
  onEditClient(clientId: number): void;
};

export const GiroPaymentManagerSummaryScreen = (props: Props) => {
  const {
    giroPaymentId,
    onSearchClient: searchClient,
    onPaymentCancelled,
    onPaymentConfirmed,
    onEditRate: editRate,
    onViewClientDetails: viewClientDetails,
    onEditClient: editClient,
  } = props;

  useGiroPaymentManagerDataProcess(giroPaymentId);

  const { downloadBillReport } = useServices();

  const { injectPromise } = useAsyncBoundary();

  const services = useServices();

  const cancelPayment = React.useCallback(() => {
    const promise = services
      .cancelGiroPayment({ giroPaymentId })
      .then(onPaymentCancelled);

    injectPromise(promise);
  }, [giroPaymentId, injectPromise, onPaymentCancelled, services]);

  const confirmPayment = React.useCallback(() => {
    const promise = services
      .confirmGiroPayment({ giroPaymentId })
      .then(onPaymentConfirmed);

    injectPromise(promise);
  }, [giroPaymentId, injectPromise, onPaymentConfirmed, services]);

  const {
    data: { giroPayment, balance, m8DebitDTOs, m8UserLegalRequisites },
  } = useGiroPaymentManager();

  const openBillReport = React.useCallback(
    (billId: number) => {
      downloadBillReport({
        billId,
        clientId: giroPayment.clientId,
      });
    },
    [giroPayment.clientId, downloadBillReport]
  );
  const { active: asyncActive } = useAsyncBoundary();

  const {
    shared: sharedDictionary,
    giroPaymentManager: { summaryScreen: ownDictionary },
  } = useIntlDictionary();

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.titleText(giroPayment.id)}
          </TextComponent>
        }
        actions={null}
        navigation={null}
      />
      <SidebarModalSectionComponent
        content={
          <>
            <TextComponent size="mp" weight="medium">
              {ownDictionary.detailsTable.titleText}
            </TextComponent>
            <MarginComponent top={20} />
            <SmallTableWrapperComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.recievedAtLabel}
                </TextComponent>
                <TextComponent>
                  {giroPayment.recievedAt
                    ? sharedDictionary.dates.calendarDateWithTime(
                        new Date(giroPayment.recievedAt)
                      )
                    : '-'}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.recievedAmountLabel}
                </TextComponent>
                <TextComponent>
                  {giroPayment.creditedTransaction
                    ? sharedDictionary.paymentTransactionText(
                        giroPayment.creditedTransaction
                      )
                    : '-'}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.creditedAmountLabel}
                </TextComponent>
                <TextComponent>
                  {sharedDictionary.paymentTransactionText(
                    giroPayment.recievedTransaction
                  )}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.exchangeRateLabel}
                </TextComponent>
                <TextComponent>
                  <TextComponent>
                    {giroPayment.exchangeRate
                      ? sharedDictionary.exhangeRateText(
                          giroPayment.exchangeRate
                        )
                      : '-'}
                  </TextComponent>{' '}
                  {(giroPayment.status === 'FAILED' ||
                    giroPayment.status === 'CLIENT_FOUND' ||
                    giroPayment.status === 'CLIENT_NOT_FOUND') && (
                    <TextComponent>
                      <ClickControlComponent onClick={editRate}>
                        <TextComponent color="accent">
                          {ownDictionary.detailsTable.editRateText}
                        </TextComponent>
                      </ClickControlComponent>
                    </TextComponent>
                  )}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.statusLabel}
                </TextComponent>
                {giroPayment.status === 'CLIENT_FOUND' ? (
                  <ThemeProviderComponent>
                    <TextComponent color="successful">
                      {sharedDictionary.giroPaymentStatusValue(
                        giroPayment.status
                      )}
                    </TextComponent>
                  </ThemeProviderComponent>
                ) : (
                  <TextComponent
                    color={
                      giroPayment.status === 'CLIENT_NOT_FOUND'
                        ? 'critical'
                        : giroPayment.status === 'FAILED'
                        ? 'secondary'
                        : 'primary'
                    }
                  >
                    {sharedDictionary.giroPaymentStatusValue(
                      giroPayment.status
                    )}
                  </TextComponent>
                )}
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.updatedAtLabel}
                </TextComponent>
                <TextComponent>
                  {giroPayment.updatedAt
                    ? sharedDictionary.dates.calendarDateWithTime(
                        new Date(giroPayment.updatedAt)
                      )
                    : '-'}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.createdAtLabel}
                </TextComponent>
                <TextComponent>
                  {sharedDictionary.dates.calendarDateWithTime(
                    new Date(giroPayment.createdAt)
                  )}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.idLabel}
                </TextComponent>
                <TextComponent>{giroPayment.id}</TextComponent>
              </SmallTableRowComponent>
            </SmallTableWrapperComponent>
          </>
        }
        actions={
          <>
            {(giroPayment.status === 'CLIENT_NOT_FOUND' ||
              giroPayment.status === 'FAILED') && (
              <ModalPressControlComponent
                type="button"
                theme="secondary"
                disabled={asyncActive}
                onPress={searchClient}
              >
                {ownDictionary.searchClientButtonText}
              </ModalPressControlComponent>
            )}
            {giroPayment.status === 'CLIENT_FOUND' && (
              <>
                <MarginComponent top={20} />
                <ModalPressControlComponent
                  type="button"
                  theme="secondary"
                  disabled={asyncActive}
                  onPress={confirmPayment}
                >
                  {ownDictionary.confirmPaymentButtonText}
                </ModalPressControlComponent>
              </>
            )}
            {giroPayment.status === 'CLIENT_FOUND' && (
              <>
                <MarginComponent top={20} />
                <ModalPressControlComponent
                  type="button"
                  theme="secondary"
                  disabled={asyncActive}
                  onPress={() => editClient(giroPayment.clientId)}
                >
                  {ownDictionary.editClientButtonText}
                </ModalPressControlComponent>
              </>
            )}
            {(giroPayment.status === 'CLIENT_NOT_FOUND' ||
              giroPayment.status === 'CLIENT_FOUND') && (
              <>
                <MarginComponent top={20} />
                <ModalPressControlComponent
                  type="button"
                  theme="secondary"
                  disabled={asyncActive}
                  onPress={cancelPayment}
                >
                  {ownDictionary.cancelPaymentButtonText}
                </ModalPressControlComponent>
              </>
            )}
          </>
        }
      />
      {giroPayment.clientId > 0 && (
        <SidebarModalSectionComponent
          content={
            <>
              <TextComponent size="mp" weight="medium">
                {ownDictionary.clientTable.titleText}
              </TextComponent>
              <MarginComponent top={20} />
              <SmallTableWrapperComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.organizationLabel}
                  </TextComponent>
                  <TextComponent>
                    {giroPayment.org.name ? (
                      <ClickControlComponent
                        onClick={() => viewClientDetails(giroPayment.clientId)}
                      >
                        <TextComponent color="accent">
                          {giroPayment.org.name}
                        </TextComponent>
                      </ClickControlComponent>
                    ) : (
                      '-'
                    )}
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.fullNameLabel}
                  </TextComponent>
                  <TextComponent>
                    {normalizeProfileLike(giroPayment.person).toFullName() ||
                      '-'}
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.unpLabel}
                  </TextComponent>
                  <TextComponent>
                    {m8UserLegalRequisites?.unp || '-'}
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.clientIdLabel}
                  </TextComponent>
                  <TextComponent>{giroPayment.clientId || '-'}</TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.residentLabel}
                  </TextComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.residentValue(
                      giroPayment.org.location === RESIDENT_COUNTRY_CODE
                    )}
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.clientTable.balanceLabel}
                  </TextComponent>
                  <TextComponent>
                    {sharedDictionary.moneyAmountText(balance, 'USD')}
                  </TextComponent>
                </SmallTableRowComponent>
                {m8DebitDTOs.length > 0 && (
                  <SmallTableRowComponent>
                    <TextComponent>
                      {ownDictionary.clientTable.debitsLabel}
                    </TextComponent>
                    <TextComponent>
                      {m8DebitDTOs.map((m8DebitDTO) => (
                        <GiroPaymentManagerBillRow
                          key={m8DebitDTO.id}
                          m8DebitDTO={m8DebitDTO}
                          onManageBill={openBillReport}
                        />
                      ))}
                    </TextComponent>
                  </SmallTableRowComponent>
                )}
              </SmallTableWrapperComponent>
            </>
          }
          actions={null}
        />
      )}
      <SidebarModalSectionComponent
        content={
          <>
            <TextComponent size="mp" weight="medium">
              {ownDictionary.paymentTable.titleText}
            </TextComponent>
            <MarginComponent top={20} />
            <SmallTableWrapperComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.paymentTable.bankAccountNameLabel}
                </TextComponent>
                <TextComponent>{giroPayment.from.name}</TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.paymentTable.bankAccountUnpLabel}
                </TextComponent>
                <TextComponent>{giroPayment.from.unp}</TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.paymentTable.commentLabel}
                </TextComponent>
                <TextComponent>{giroPayment.comment}</TextComponent>
              </SmallTableRowComponent>
            </SmallTableWrapperComponent>
          </>
        }
        actions={null}
      />
    </>
  );
};
